import type { DrawingDocument, ScaleState } from '../contract/types.js';
import { assertContractVersion } from '../contract/version.js';
import type { MatcherProfile, PageAlignmentRule } from './diff.js';

/**
 * Who is being compared, and whether they may be.
 *
 * A comparison is only meaningful between two versions of **one drawing owned
 * by one tenant**. Nothing in the Drawing2D contract carries that: a
 * `DrawingDocument` knows its own geometry and provenance and nothing about
 * which document or which customer it belongs to. So the caller supplies it,
 * and this module refuses a pair that does not hold together rather than
 * comparing two unrelated drawings and returning a plausible answer.
 *
 * The prototype has no server and no tenant boundary to enforce — processing is
 * entirely client-side. What this gives instead is the contract-level guard the
 * server side would call: the shape of the identity, the refusals with their
 * intended external status, and a storage key that makes an ordered pair
 * idempotent. Whoever implements the API implements these rules, not their own.
 */

export interface VersionRef {
  tenantId: string;
  documentId: string;
  documentVersionId: string;
  /** sha256 of this version's `drawing.json`. Empty means it was never built. */
  artefactSha256: string;
  /** sha256 of the source PDF, where the caller knows it. */
  sourceChecksum?: string;
}

/** The closed set of reasons a pair is refused. Refusals are classified, never free text. */
export const DIFF_PAIR_REFUSALS = [
  'DIFF_PAIR_CROSS_TENANT',
  'DIFF_PAIR_UNRELATED_DOCUMENT',
  'DIFF_PAIR_ARTEFACT_MISSING',
  'DIFF_PAIR_CONTRACT_VERSION_UNSUPPORTED',
  'DIFF_PAIR_CONTRACT_VERSION_MISMATCH',
] as const;

export type DiffPairRefusal = (typeof DIFF_PAIR_REFUSALS)[number];

export class VersionPairError extends Error {
  constructor(
    readonly code: DiffPairRefusal,
    /**
     * What the outside world should be told.
     *
     * A cross-tenant or unrelated-document request is answered **404**, not 403:
     * telling a caller that a document exists but is not theirs is itself a
     * disclosure. A malformed request of their own documents is 422.
     */
    readonly externalStatus: 404 | 422,
    message: string,
  ) {
    super(message);
    this.name = 'VersionPairError';
  }
}

export interface DiffSideIdentity {
  documentVersionId: string;
  artefactSha256: string;
  sourceChecksum?: string;
  contractVersion: string;
  extractorBundleVersion: string;
  scaleState: ScaleState;
}

export interface DiffIdentity {
  tenantId: string;
  documentId: string;
  base: DiffSideIdentity;
  compare: DiffSideIdentity;
  matcherProfileVersion: string;
  pageAlignmentRule: PageAlignmentRule;
  schemaVersion: string;
  /**
   * The identity of this comparison as a stored thing.
   *
   * Ordered: comparing A to B is a different result from comparing B to A, so
   * it must be a different record. Everything that can change the answer is in
   * the key — both artefact hashes, the matcher profile and the schema — so a
   * re-run with the same inputs is the same record rather than a second one.
   */
  storageKey: string;
  /**
   * True when the two versions were extracted by different bundles.
   *
   * Not a refusal, because a customer's two versions may legitimately have been
   * processed months apart. But extraction is only stable *within* a version of
   * the libraries, so a bundle difference can manufacture changes nobody made,
   * and a reviewer looking at a surprising diff deserves to be told.
   */
  extractorBundleMismatch: boolean;
}

export function validateVersionPair(
  base: { ref: VersionRef; document: DrawingDocument },
  compare: { ref: VersionRef; document: DrawingDocument },
  profile: MatcherProfile,
): DiffIdentity {
  if (base.ref.tenantId !== compare.ref.tenantId) {
    throw new VersionPairError(
      'DIFF_PAIR_CROSS_TENANT',
      404,
      'the two versions belong to different tenants and cannot be compared',
    );
  }
  if (base.ref.documentId !== compare.ref.documentId) {
    throw new VersionPairError(
      'DIFF_PAIR_UNRELATED_DOCUMENT',
      404,
      'the two versions belong to different documents; a comparison is only defined within one',
    );
  }
  if (base.ref.artefactSha256 === '' || compare.ref.artefactSha256 === '') {
    throw new VersionPairError(
      'DIFF_PAIR_ARTEFACT_MISSING',
      422,
      'one side has no built artefact to compare; a missing artefact is not an empty drawing',
    );
  }

  for (const doc of [base.document, compare.document]) {
    try {
      assertContractVersion(doc.contractVersion);
    } catch (err) {
      throw new VersionPairError(
        'DIFF_PAIR_CONTRACT_VERSION_UNSUPPORTED',
        422,
        err instanceof Error ? err.message : String(err),
      );
    }
  }

  if (base.document.contractVersion !== compare.document.contractVersion) {
    throw new VersionPairError(
      'DIFF_PAIR_CONTRACT_VERSION_MISMATCH',
      422,
      `contract versions differ: ${base.document.contractVersion} against ` +
        `${compare.document.contractVersion}; the comparison would report one of them as if it ` +
        'described both',
    );
  }

  const identity: DiffIdentity = {
    tenantId: base.ref.tenantId,
    documentId: base.ref.documentId,
    base: sideIdentity(base.ref, base.document),
    compare: sideIdentity(compare.ref, compare.document),
    matcherProfileVersion: profile.version,
    pageAlignmentRule: profile.pageAlignment,
    schemaVersion: base.document.contractVersion,
    storageKey: '',
    extractorBundleMismatch:
      base.document.provenance.extractorBundleVersion !==
      compare.document.provenance.extractorBundleVersion,
  };
  identity.storageKey = storageKeyFor(identity);
  return identity;
}

function sideIdentity(ref: VersionRef, doc: DrawingDocument): DiffSideIdentity {
  return {
    documentVersionId: ref.documentVersionId,
    artefactSha256: ref.artefactSha256,
    ...(ref.sourceChecksum === undefined ? {} : { sourceChecksum: ref.sourceChecksum }),
    contractVersion: doc.contractVersion,
    extractorBundleVersion: doc.provenance.extractorBundleVersion,
    scaleState: doc.scaleState,
  };
}

export function storageKeyFor(identity: DiffIdentity): string {
  return [
    identity.tenantId,
    identity.documentId,
    `${identity.base.documentVersionId}..${identity.compare.documentVersionId}`,
    `${identity.base.artefactSha256.slice(0, 16)}-${identity.compare.artefactSha256.slice(0, 16)}`,
    // The alignment rule is named as well as the version, because it changes the
    // answer and this repository's own runner varies it while holding the
    // version fixed — it measures all three rules against each other to justify
    // the default. Without the rule in the key those runs collide on one record,
    // and a store keyed on it returns the wrong comparison or overwrites one
    // with another. Two answers, one key, is the failure the key exists to
    // prevent.
    `matcher-${identity.matcherProfileVersion}-${identity.pageAlignmentRule}`,
    `schema-${identity.schemaVersion}`,
  ].join('/');
}
